(() => {
  const parseMoneyFormat = (raw) => {
    if (!raw) return '{{amount}}';
    try {
      const once = JSON.parse(raw);
      return typeof once === 'string' && once.startsWith('"') ? JSON.parse(once) : once;
    } catch (_error) {
      return raw.replace(/^"|"$/g, '').replaceAll('&quot;', '"');
    }
  };

  const formatMoney = (cents, format) => {
    const value = Number(cents || 0);
    const token = (format.match(/{{\s*(\w+)\s*}}/) || [null, 'amount'])[1];
    const withDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      const fixed = (Number(number) / 100).toFixed(precision);
      const parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    };
    const replacements = {
      amount: withDelimiters(value),
      amount_no_decimals: withDelimiters(value, 0),
      amount_with_comma_separator: withDelimiters(value, 2, '.', ','),
      amount_no_decimals_with_comma_separator: withDelimiters(value, 0, '.', ','),
      amount_with_apostrophe_separator: withDelimiters(value, 2, "'", '.'),
      amount_no_decimals_with_space_separator: withDelimiters(value, 0, ' ', '.'),
      amount_with_space_separator: withDelimiters(value, 2, ' ', ','),
      amount_with_period_and_space_separator: withDelimiters(value, 2, ' ', '.')
    };
    let rendered = format.replace(/{{\s*\w+\s*}}/, replacements[token] ?? replacements.amount);
    rendered = rendered.replace(/([.,])00(?=\D*$)/, '');
    const holder = document.createElement('textarea');
    holder.innerHTML = rendered;
    return holder.value;
  };

  const initGallery = (root) => {
    if (root.dataset.aocdxGalleryReady === 'true') return;
    root.dataset.aocdxGalleryReady = 'true';
    const slides = [...root.querySelectorAll('[data-aocdx-gallery-slide]')];
    const thumbs = [...root.querySelectorAll('[data-aocdx-gallery-thumb]')];
    const dots = [...root.querySelectorAll('[data-aocdx-gallery-dot]')];
    const stage = root.querySelector('.aocdx-gallery__stage');
    const status = root.querySelector('[data-aocdx-gallery-status]');
    const previousButton = root.querySelector('[data-aocdx-gallery-prev]');
    const nextButton = root.querySelector('[data-aocdx-gallery-next]');
    const sectionRoot = root.closest('[data-aocdx-root]');
    const controller = new AbortController();
    const { signal } = controller;
    let currentIndex = 0;
    let swipeStart = null;
    let variantMediaMap = {};

    try {
      variantMediaMap = JSON.parse(root.querySelector('[data-aocdx-variant-media-map]')?.textContent || '{}');
    } catch (_error) {
      variantMediaMap = {};
    }

    const pauseMedia = (slide) => {
      slide.querySelectorAll('video').forEach((video) => video.pause());
      slide.querySelectorAll('model-viewer').forEach((model) => model.pause?.());
    };

    const activate = (mediaId, {
      announce = true,
      focusThumb = false,
      focusDot = false,
      scrollThumb = true
    } = {}) => {
      const index = slides.findIndex((slide) => slide.dataset.aocdxGallerySlide === String(mediaId));
      if (index < 0) return false;
      const activeRatio = Number.parseFloat(slides[index].dataset.aocdxMediaRatio);
      if (stage && Number.isFinite(activeRatio) && activeRatio > 0) {
        stage.style.setProperty('--aocdx-active-media-ratio', String(activeRatio));
      }
      const activeMobileRatio = Number.parseFloat(slides[index].dataset.aocdxMediaRatioMobile);
      if (stage && Number.isFinite(activeMobileRatio) && activeMobileRatio > 0) {
        stage.style.setProperty('--aocdx-active-media-ratio-mobile', String(activeMobileRatio));
      }
      currentIndex = index;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.hidden = !active;
        slide.setAttribute('aria-hidden', String(!active));
        if (!active) pauseMedia(slide);
      });
      thumbs.forEach((thumb) => {
        const active = thumb.dataset.aocdxGalleryThumb === String(mediaId);
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-selected', String(active));
        // A tab carries aria-selected only; a thumbnail that is not a tab keeps its pressed state.
        if (thumb.getAttribute('role') === 'tab') thumb.removeAttribute('aria-pressed');
        else thumb.setAttribute('aria-pressed', String(active));
        thumb.tabIndex = active ? 0 : -1;
      });
      const activeThumb = thumbs.find((thumb) => thumb.dataset.aocdxGalleryThumb === String(mediaId));
      if (scrollThumb) activeThumb?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      if (focusThumb) activeThumb?.focus({ preventScroll: true });
      dots.forEach((dot) => {
        const active = dot.dataset.aocdxGalleryDot === String(mediaId);
        dot.classList.toggle('is-active', active);
        dot.tabIndex = active ? 0 : -1;
        if (dot.getAttribute('role') === 'tab') {
          // Legacy aocdx-buy pagination dots (role="tab") keep their original states.
          dot.setAttribute('aria-selected', String(active));
          dot.setAttribute('aria-pressed', String(active));
        } else if (active) {
          // Position dots (ao-gnav): aria-current only, never aria-selected / aria-pressed.
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
      const activeDot = dots.find((dot) => dot.dataset.aocdxGalleryDot === String(mediaId));
      if (focusDot) activeDot?.focus({ preventScroll: true });
      if (announce && status) {
        status.textContent = `Image ${index + 1} of ${slides.length}`;
      }
      return true;
    };

    const move = (direction, options = {}) => {
      if (slides.length < 2) return;
      const nextIndex = (currentIndex + direction + slides.length) % slides.length;
      activate(slides[nextIndex].dataset.aocdxGallerySlide, options);
    };

    const releaseSwipePointer = (pointerId) => {
      if (stage?.hasPointerCapture?.(pointerId)) stage.releasePointerCapture(pointerId);
    };

    const handleNavigationKey = (event, focusTarget = '') => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const focusThumb = focusTarget === 'thumb';
      const focusDot = focusTarget === 'dot';
      if (event.key === 'Home') {
        activate(slides[0].dataset.aocdxGallerySlide, { focusThumb, focusDot });
      } else if (event.key === 'End') {
        activate(slides.at(-1).dataset.aocdxGallerySlide, { focusThumb, focusDot });
      } else {
        move(event.key === 'ArrowRight' ? 1 : -1, { focusThumb, focusDot });
      }
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => activate(thumb.dataset.aocdxGalleryThumb), { signal });
      thumb.addEventListener('keydown', (event) => handleNavigationKey(event, 'thumb'), { signal });
    });
    dots.forEach((dot) => {
      dot.addEventListener('click', () => activate(dot.dataset.aocdxGalleryDot), { signal });
      dot.addEventListener('keydown', (event) => handleNavigationKey(event, 'dot'), { signal });
    });
    previousButton?.addEventListener('click', () => move(-1), { signal });
    nextButton?.addEventListener('click', () => move(1), { signal });
    stage?.addEventListener('keydown', (event) => handleNavigationKey(event), { signal });
    stage?.addEventListener('pointerdown', (event) => {
      if (event.target.closest('video, iframe, model-viewer, button, a, input, select, textarea')) return;
      swipeStart = { x: event.clientX, y: event.clientY, pointerId: event.pointerId };
      try {
        stage.setPointerCapture?.(event.pointerId);
      } catch (_error) {
        // A pointer that is no longer active (or a synthetic one) cannot be captured; the swipe still resolves on pointerup.
      }
    }, { signal });
    stage?.addEventListener('pointerup', (event) => {
      if (!swipeStart || event.pointerId !== swipeStart.pointerId) return;
      const deltaX = event.clientX - swipeStart.x;
      const deltaY = event.clientY - swipeStart.y;
      releaseSwipePointer(event.pointerId);
      swipeStart = null;
      if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;
      move(deltaX < 0 ? 1 : -1);
    }, { signal });
    stage?.addEventListener('pointercancel', (event) => {
      releaseSwipePointer(event.pointerId);
      swipeStart = null;
    }, { signal });

    const activateVariantMedia = (variant, variantId) => {
      const mediaId = variant?.featured_media?.id
        || variant?.featuredMedia?.id
        || variantMediaMap[String(variant?.id || variantId || '')];
      if (mediaId) activate(mediaId);
    };
    const handleVariantEvent = (event) => {
      const detail = event.detail || {};
      activateVariantMedia(detail.variant || detail.currentVariant || detail, detail.variantId);
    };
    ['variant:change', 'variant-change', 'product:variant-change'].forEach((eventName) => {
      document.addEventListener(eventName, handleVariantEvent, { signal });
    });
    sectionRoot?.addEventListener('change', (event) => {
      if (!event.target.matches('input[name="id"], select[name="id"]')) return;
      activateVariantMedia(null, event.target.value);
    }, { signal });

    const initialMediaId = root.dataset.aocdxInitialMediaId || slides[0]?.dataset.aocdxGallerySlide;
    if (initialMediaId) activate(initialMediaId, { announce: false, scrollThumb: false });
    root.aocdxActivateGalleryMedia = activate;
    sectionRoot && (sectionRoot.aocdxCleanups ||= []).push(() => {
      controller.abort();
      delete root.aocdxActivateGalleryMedia;
    });
  };

  const initFaqs = (root) => {
    if (root.dataset.aocdxFaqReady === 'true') return;
    const items = [...root.querySelectorAll('[data-aocdx-faq-item]')];
    if (!items.length) return;
    root.dataset.aocdxFaqReady = 'true';
    const controller = new AbortController();
    const { signal } = controller;

    const applyExpandedState = (item, expanded) => {
      const button = item.querySelector('[data-aocdx-faq-button]');
      const panel = button && document.getElementById(button.getAttribute('aria-controls'));
      const icon = item.querySelector('[data-aocdx-faq-icon]');
      const state = item.querySelector('[data-aocdx-faq-state]');
      if (!button || !panel) return;
      button.setAttribute('aria-expanded', String(expanded));
      panel.hidden = !expanded;
      item.classList.toggle('is-open', expanded);
      if (icon) icon.textContent = expanded ? '−' : '+';
      if (state) state.textContent = expanded ? 'Collapse answer' : 'Expand answer';
    };

    const setExpanded = (item, expanded, { closeOthers = true } = {}) => {
      if (!item || !items.includes(item)) return;
      if (expanded && closeOthers) {
        items.forEach((other) => {
          if (other !== item) applyExpandedState(other, false);
        });
      }
      applyExpandedState(item, expanded);
    };

    root.aocdxSetFaqExpanded = setExpanded;
    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-aocdx-faq-button]');
      if (!button || !root.contains(button)) return;
      const item = button.closest('[data-aocdx-faq-item]');
      setExpanded(item, button.getAttribute('aria-expanded') !== 'true');
    }, { signal });
    root.addEventListener('keydown', (event) => {
      if (!['Enter', ' '].includes(event.key)) return;
      const button = event.target.closest('[data-aocdx-faq-button]');
      if (!button || !root.contains(button)) return;
      event.preventDefault();
      const item = button.closest('[data-aocdx-faq-item]');
      setExpanded(item, button.getAttribute('aria-expanded') !== 'true');
    }, { signal });

    root.aocdxCleanups ||= [];
    root.aocdxCleanups.push(() => {
      controller.abort();
      delete root.dataset.aocdxFaqReady;
      delete root.aocdxSetFaqExpanded;
    });
  };

  const initMenu = (root) => {
    const button = root.querySelector('[data-aocdx-menu-toggle]');
    const menu = root.querySelector('[data-aocdx-mobile-nav]');
    if (!button || !menu) return;
    const setOpen = (open) => {
      button.setAttribute('aria-expanded', String(open));
      menu.classList.toggle('is-open', open);
    };
    button.addEventListener('click', () => setOpen(button.getAttribute('aria-expanded') !== 'true'));
    menu.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => setOpen(false)));
  };

  const initWheel = () => {
    document.querySelectorAll('[data-aocdx-wheel]').forEach((wheel) => {
      if (wheel.dataset.aocdxWheelReady === 'true') return;
      wheel.dataset.aocdxWheelReady = 'true';
      const wedges = [...wheel.querySelectorAll('[data-aocdx-wheel-index]')];
      const labels = [...wheel.parentElement.querySelectorAll('[data-aocdx-wheel-label]')];
      const activate = (index) => {
        wedges.forEach((wedge, wedgeIndex) => {
          const active = wedgeIndex === index;
          wedge.classList.toggle('is-active', active);
          wedge.setAttribute('aria-pressed', String(active));
        });
        labels.forEach((label, labelIndex) => label.classList.toggle('is-active', labelIndex === index));
      };
      wedges.forEach((wedge, index) => {
        wedge.addEventListener('click', () => activate(index));
        wedge.addEventListener('focus', () => activate(index));
        wedge.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'touch') activate(index);
        });
      });
      labels.forEach((label, index) => {
        label.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'touch') activate(index);
        });
      });
      activate(0);
    });
  };

  const openCart = (trigger) => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open(trigger);
      return;
    }
    const nativeCart = document.querySelector('#cart-icon-bubble, a[href$="/cart"]');
    nativeCart?.click();
  };

  const fetchCart = async (timeout = 3000) => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        signal: controller.signal
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (_error) {
      return null;
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const syncCartCount = (count) => {
    const safeCount = Number.isFinite(Number(count)) ? Number(count) : 0;
    if (window.AofSharedChrome?.setCartCount) {
      window.AofSharedChrome.setCartCount(safeCount);
      return;
    }
    document.querySelectorAll('[data-aof-cart-count], [data-aocdx-cart-count]').forEach((node) => {
      node.textContent = String(safeCount);
    });
    document.querySelectorAll('[data-aof-cart-label], [data-aocdx-cart-label]').forEach((node) => {
      node.removeAttribute('aria-label');
    });
  };

  const readCartCount = async (root) => {
    try {
      const cart = await fetchCart();
      if (!cart) return null;
      syncCartCount(cart.item_count);
      return cart;
    } catch (_error) {
      // The native cart UI still owns the authoritative state.
      return null;
    }
  };

  const initSticky = (root) => {
    const buy = root.querySelector('[data-aocdx-buy]');
    const sticky = root.querySelector('[data-aocdx-sticky]');
    if (!buy || !sticky || !('IntersectionObserver' in window)) return;
    let buyVisible = true;
    let closingVisible = false;
    let closingObserver;
    const update = () => {
      const visible = !buyVisible && !closingVisible;
      sticky.classList.toggle('is-visible', visible);
      sticky.toggleAttribute('inert', !visible);
      sticky.setAttribute('aria-hidden', String(!visible));
    };
    const buyObserver = new IntersectionObserver((entries) => {
      buyVisible = entries[0]?.isIntersecting ?? true;
      update();
    }, { rootMargin: '-120px 0px 0px 0px', threshold: 0 });
    buyObserver.observe(buy);
    const refreshClosing = (excludedScope) => {
      closingObserver?.disconnect();
      closingObserver = undefined;
      closingVisible = false;
      const closing = [...document.querySelectorAll('[data-aocdx-closing]')].find((node) => {
        if (!excludedScope) return true;
        return node !== excludedScope && !excludedScope.contains?.(node);
      });
      if (closing) {
        closingObserver = new IntersectionObserver((entries) => {
          closingVisible = entries[0]?.isIntersecting ?? false;
          update();
        }, { threshold: 0.08 });
        closingObserver.observe(closing);
      }
      update();
    };
    root.aocdxRefreshSticky = refreshClosing;
    refreshClosing();
    root.aocdxCleanups ||= [];
    root.aocdxCleanups.push(() => {
      buyObserver.disconnect();
      closingObserver?.disconnect();
      sticky.classList.remove('is-visible');
      sticky.setAttribute('inert', '');
      sticky.setAttribute('aria-hidden', 'true');
      delete root.aocdxRefreshSticky;
    });
  };

  const initCommerce = (root) => {
    if (root.dataset.aocdxCommerceReady === 'true') return;
    root.dataset.aocdxCommerceReady = 'true';
    root.aocdxCleanups ||= [];

    const format = parseMoneyFormat(root.dataset.aocdxMoneyFormat);
    const plans = [...root.querySelectorAll('[data-aocdx-plan]')];
    const status = root.querySelector('[data-aocdx-status]');
    const nativeRoot = root.querySelector('.aocdx-native-commerce');
    const nativeSubmit = nativeRoot?.querySelector('[id^="ProductSubmitButton-"]');
    const oilVariantId = String(root.dataset.aocdxOilVariant || '').trim();
    const protocolVariantId = String(root.dataset.aocdxProtocolVariant || '').trim();
    const validVariantId = (value) => /^\d+$/.test(value) && value !== '0';
    const oilAvailable = root.dataset.aocdxOilAvailable === 'true';
    const protocolAvailable = root.dataset.aocdxProtocolAvailable === 'true';
    const nativeVariant = nativeRoot?.querySelector('.product-variant-id');
    const nativeRadios = [1, 2, 3].map((index) => nativeRoot?.querySelector(`input[name="quantity"][data-input="input_${index}"]`));
    const nativeGiftOffers = [...(nativeRoot?.querySelectorAll('quantity-break-upsells[data-offer-index-unlock="true"][data-offer]') || [])];
    const copy = {
      protocolUnavailable: root.dataset.aocdxStatusProtocol || 'The Hair Density Protocol is currently unavailable. The single-oil plan remains available.',
      productUnavailable: root.dataset.aocdxStatusProduct || 'This product is currently unavailable.',
      configError: root.dataset.aocdxStatusConfig || 'Purchasing is paused because the expected Shopify cart configuration could not be verified.',
      planUnavailable: root.dataset.aocdxStatusPlan || 'This treatment plan is not currently available.',
      adding: root.dataset.aocdxStatusAdding || 'Adding your selected treatment to the cart…',
      cartError: root.dataset.aocdxStatusCartError || 'The cart did not update. Please try again.',
      cartReview: root.dataset.aocdxStatusCartReview || 'We could not confirm the cart update. Opening your cart so you can review it before trying again.'
    };
    const oilBridgeValid = validVariantId(oilVariantId) && nativeVariant?.value === oilVariantId;
    const protocolOfferMap = new Map(nativeGiftOffers.map((container) => [
      Number(container.dataset.offer),
      [...container.querySelectorAll('.quantity-break__upsell[data-product]')]
        .filter((item) => item.dataset.type === 'gift' && item.dataset.selected === 'true')
        .map((item) => item.dataset.product)
    ]));
    const protocolOfferIds = nativeGiftOffers
      .map((container) => Number(container.dataset.offer))
      .sort((a, b) => a - b);
    const protocolBridgeValid = validVariantId(protocolVariantId)
      && nativeGiftOffers.length === 3
      && protocolOfferMap.size === 3
      && protocolOfferIds.join('/') === '1/2/3'
      && (protocolOfferMap.get(1) || []).length === 0
      && [2, 3].every((offer) => {
        const variants = protocolOfferMap.get(offer) || [];
        return variants.length === 1 && variants[0] === protocolVariantId;
      });
    const visiblePlanIds = plans.map((plan) => Number(plan.dataset.aocdxPlan)).sort((a, b) => a - b);
    const visiblePlanSetValid = plans.length === 3 && visiblePlanIds.join('/') === '1/2/3';
    const planBridgeValid = nativeRadios.every(Boolean)
      && nativeRadios.map((radio) => radio.value).join('/') === '1/1/2'
      && visiblePlanSetValid;
    const searchParams = new URLSearchParams(window.location.search);
    const namedPlans = { oil: 1, complete: 2, full: 3 };
    const namedPlan = namedPlans[String(searchParams.get('plan') || '').toLowerCase()];
    const legacyPlan = Number(searchParams.get('aocdx_plan'));
    const initialPlan = [1, 2, 3].includes(namedPlan)
      ? namedPlan
      : ([1, 2, 3].includes(legacyPlan) ? legacyPlan : 1);
    let currentPlan = initialPlan;
    let busy = false;

    if (nativeRoot) {
      nativeRoot.querySelectorAll('button, input, select, textarea, a').forEach((node) => node.setAttribute('tabindex', '-1'));
      if (nativeVariant && validVariantId(oilVariantId)) nativeVariant.value = oilVariantId;
    }

    const showStatus = (message) => {
      if (!status) return;
      status.textContent = message;
      status.hidden = !message;
    };

    const nativePlan = (index) => nativeRadios[index - 1];
    const currentPlanReady = () => oilAvailable
      && oilBridgeValid
      && planBridgeValid
      && Boolean(nativeSubmit)
      && (currentPlan === 1 || (protocolAvailable && protocolBridgeValid));

    const purchaseButtons = () => [
      ...root.querySelectorAll('[data-aocdx-submit], [data-aocdx-buy-now]'),
      ...document.querySelectorAll('.aocdx-closing [data-aocdx-submit]')
    ];

    const updatePurchaseState = () => {
      const ready = currentPlanReady() && !busy;
      purchaseButtons().forEach((button) => {
        button.disabled = !ready;
      });
      root.toggleAttribute('aria-busy', busy);
      plans.forEach((plan) => plan.setAttribute('aria-disabled', String(busy || plan.disabled)));
    };

    const updatePlan = (index, focus = false) => {
      if (busy) return;
      const plan = plans.find((item) => Number(item.dataset.aocdxPlan) === index);
      if (!plan || plan.disabled) return;
      currentPlan = index;
      plans.forEach((item) => {
        const active = item === plan;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-checked', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      const radio = nativePlan(index);
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const priceCents = Number(plan.dataset.price || 0);
      const compareCents = Number(plan.dataset.compare || 0);
      const savingCents = Math.max(0, compareCents - priceCents);
      const hasSavings = savingCents > 0;
      const price = formatMoney(priceCents, format);
      const compare = formatMoney(compareCents, format);
      const saved = formatMoney(savingCents, format);
      root.querySelectorAll('[data-aocdx-current-price], [data-aocdx-selected-price], [data-aocdx-sticky-price]').forEach((node) => { node.textContent = price; });
      root.querySelectorAll('[data-aocdx-current-compare], [data-aocdx-sticky-compare]').forEach((node) => {
        node.textContent = compare;
        node.hidden = !hasSavings;
      });
      root.querySelectorAll('[data-aocdx-current-save]').forEach((node) => {
        node.textContent = `Save ${saved} vs. full retail value`;
        node.hidden = !hasSavings;
      });
      root.querySelectorAll('[data-aocdx-selected-name], [data-aocdx-sticky-name]').forEach((node) => { node.textContent = plan.dataset.name; });
      root.querySelectorAll('[data-aocdx-selected-items]').forEach((node) => { node.textContent = `· ${plan.dataset.items}`; });
      root.querySelectorAll('[data-aocdx-atc-price]').forEach((node) => { node.textContent = price; });
      document.querySelectorAll('[data-aocdx-closing-price]').forEach((node) => { node.textContent = price; });
      updatePurchaseState();
      if (focus) plan.focus();
    };

    if (!protocolAvailable || !protocolBridgeValid) {
      plans.filter((plan) => Number(plan.dataset.aocdxPlan) > 1).forEach((plan) => {
        plan.disabled = true;
        plan.setAttribute('aria-disabled', 'true');
        plan.tabIndex = -1;
      });
      updatePlan(1);
      showStatus(copy.protocolUnavailable);
    } else {
      updatePlan(initialPlan);
    }

    if (!oilAvailable || !oilBridgeValid || !planBridgeValid || !nativeSubmit) {
      showStatus(!oilAvailable ? copy.productUnavailable : copy.configError);
      updatePurchaseState();
    }

    plans.forEach((plan) => {
      plan.addEventListener('click', () => {
        if (busy) return;
        updatePlan(Number(plan.dataset.aocdxPlan));
        showStatus('');
      });
      plan.addEventListener('keydown', (event) => {
        if (busy) return;
        if (!['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
        const enabledPlans = plans.filter((item) => !item.disabled);
        const currentIndex = enabledPlans.indexOf(plan);
        let targetIndex = currentIndex;
        if (event.key === 'Home') targetIndex = 0;
        else if (event.key === 'End') targetIndex = enabledPlans.length - 1;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') targetIndex = (currentIndex - 1 + enabledPlans.length) % enabledPlans.length;
        else targetIndex = (currentIndex + 1) % enabledPlans.length;
        event.preventDefault();
        updatePlan(Number(enabledPlans[targetIndex].dataset.aocdxPlan), true);
        showStatus('');
      });
    });

    const variantQuantity = (cart, variantId) => (cart?.items || [])
      .filter((item) => String(item.variant_id) === variantId)
      .reduce((total, item) => total + Number(item.quantity || 0), 0);

    const matchesSelectedPlan = (before, after, selectedPlan, verifyItemCount = true) => {
      if (!before || !after) return false;
      const expectedOil = selectedPlan === 3 ? 2 : 1;
      const expectedProtocol = selectedPlan > 1 ? 1 : 0;
      return variantQuantity(after, oilVariantId) - variantQuantity(before, oilVariantId) === expectedOil
        && variantQuantity(after, protocolVariantId) - variantQuantity(before, protocolVariantId) === expectedProtocol
        && (!verifyItemCount || after.item_count - before.item_count === expectedOil + expectedProtocol);
    };

    const readDrawerCart = () => {
      const items = [...document.querySelectorAll('cart-drawer .cart-item')].map((item) => ({
        variant_id: Number((item.dataset.lineKey || '').split(':')[0]),
        quantity: Number(item.dataset.quantity || 0)
      })).filter((item) => item.variant_id && item.quantity > 0);
      if (!items.length) return null;
      return {
        item_count: items.reduce((total, item) => total + item.quantity, 0),
        items
      };
    };

    const invokeNative = async (customButton, checkout = false) => {
      if (!nativeSubmit || customButton.disabled || busy) return;
      if (!currentPlanReady()) {
        showStatus(copy.planUnavailable);
        return;
      }

      const selectedPlan = currentPlan;
      let deferBusyReset = false;
      busy = true;
      updatePurchaseState();
      showStatus(copy.adding);

      const selected = nativePlan(selectedPlan);
      if (selected) {
        selected.checked = true;
        // Re-announce the active quantity immediately before submission. A
        // plan selected from the URL can be checked before Shrine finishes
        // registering its quantity-break listeners, especially Plan 3 (oil
        // quantity 2). Dispatching here keeps Shrine's own multi-item cart
        // mechanism authoritative without duplicating it.
        selected.dispatchEvent(new Event('change', { bubbles: true }));
      }

      try {
        const before = await fetchCart();
        if (!before) throw new Error(copy.cartError);

        const errorNode = nativeRoot?.querySelector('.product-form__error-message, [data-error-message]');
        const previousNativeError = errorNode?.textContent?.trim() || '';
        const operationDeadline = performance.now() + 10000;
        nativeSubmit.click();
        for (let attempt = 0; attempt < 32 && performance.now() < operationDeadline; attempt += 1) {
          await new Promise((resolve) => window.setTimeout(resolve, 250));
          const currentNativeError = errorNode?.textContent?.trim() || '';
          if (currentNativeError && currentNativeError !== previousNativeError) {
            throw new Error(currentNativeError);
          }

          const remainingTime = Math.max(250, operationDeadline - performance.now());
          const fetchedCart = await fetchCart(Math.min(1200, remainingTime));
          const drawerCart = readDrawerCart();
          const fetchedCartMatches = matchesSelectedPlan(before, fetchedCart, selectedPlan);
          const drawerCartMatches = matchesSelectedPlan(before, drawerCart, selectedPlan);
          const after = checkout
            ? (fetchedCartMatches ? fetchedCart : (drawerCartMatches ? drawerCart : null))
            : (drawerCartMatches ? drawerCart : null);
          if (!after) continue;

          syncCartCount(after.item_count);
          showStatus('');

          if (checkout) {
            // Theme Editor preview: checkout cannot load inside the preview frame, so show the cart instead.
            if (window.Shopify?.designMode) {
              openCart(customButton);
              return;
            }
            deferBusyReset = true;
            window.location.assign('/checkout');
            window.setTimeout(() => {
              busy = false;
              updatePurchaseState();
            }, 8000);
            return;
          }

          await new Promise((resolve) => window.setTimeout(resolve, 120));
          const nativeDrawer = document.querySelector('cart-drawer');
          if (!nativeDrawer?.classList.contains('active')) openCart(customButton);
          return;
        }

        showStatus(copy.cartReview);
        if (window.Shopify?.designMode) return;
        deferBusyReset = true;
        window.setTimeout(() => window.location.assign(window.routes?.cart_url || '/cart'), 500);
        window.setTimeout(() => {
          busy = false;
          updatePurchaseState();
        }, 3000);
      } catch (error) {
        showStatus(error?.message || copy.cartError);
        if (currentPlanReady()) {
          nativeSubmit.disabled = false;
          nativeSubmit.setAttribute('aria-disabled', 'false');
        }
      } finally {
        if (!deferBusyReset) {
          busy = false;
          updatePurchaseState();
        }
      }
    };

    const bindPurchaseButtons = () => {
      purchaseButtons().forEach((button) => {
        if (button.aocdxPurchaseHandler) button.removeEventListener('click', button.aocdxPurchaseHandler);
        const handler = () => invokeNative(button, button.matches('[data-aocdx-buy-now]'));
        button.aocdxPurchaseHandler = handler;
        button.addEventListener('click', handler);
      });
      updatePurchaseState();
    };

    bindPurchaseButtons();
    root.aocdxBindPurchaseButtons = bindPurchaseButtons;
    root.aocdxSelectPlan = updatePlan;
    root.querySelectorAll('[data-aocdx-cart-trigger]').forEach((button) => button.addEventListener('click', () => openCart(button)));

    const bindPlanSelectors = () => {
      document.querySelectorAll('[data-aocdx-select-plan]').forEach((button) => {
        if (button.aocdxSelectPlanHandler) button.removeEventListener('click', button.aocdxSelectPlanHandler);
        const handler = () => {
          updatePlan(Number(button.dataset.aocdxSelectPlan || 2));
          document.getElementById('buy')?.scrollIntoView({ behavior: root.dataset.aocdxMotion === 'false' || matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        };
        button.aocdxSelectPlanHandler = handler;
        button.addEventListener('click', handler);
      });
    };
    bindPlanSelectors();
    root.aocdxBindPlanSelectors = bindPlanSelectors;

    const cartDrawer = document.querySelector('cart-drawer');
    let cartObserver;
    if (cartDrawer && 'MutationObserver' in window) {
      let cartTimer;
      cartObserver = new MutationObserver(() => {
        window.clearTimeout(cartTimer);
        cartTimer = window.setTimeout(() => readCartCount(root), 160);
      });
      cartObserver.observe(cartDrawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    }

    const handlePageShow = () => {
      busy = false;
      updatePurchaseState();
      readCartCount(root);
    };
    window.addEventListener('pageshow', handlePageShow);
    root.aocdxCleanups.push(() => {
      cartObserver?.disconnect();
      window.removeEventListener('pageshow', handlePageShow);
      purchaseButtons().forEach((button) => {
        if (!button.aocdxPurchaseHandler) return;
        button.removeEventListener('click', button.aocdxPurchaseHandler);
        delete button.aocdxPurchaseHandler;
      });
      document.querySelectorAll('[data-aocdx-select-plan]').forEach((button) => {
        if (!button.aocdxSelectPlanHandler) return;
        button.removeEventListener('click', button.aocdxSelectPlanHandler);
        delete button.aocdxSelectPlanHandler;
      });
      delete root.aocdxBindPlanSelectors;
    });
    readCartCount(root);
  };

  const init = () => {
    document.querySelectorAll('[data-aocdx-root]').forEach((root) => {
      if (root.dataset.aocdxUiReady === 'true') {
        root.aocdxBindPurchaseButtons?.();
        root.aocdxBindPlanSelectors?.();
        root.aocdxRefreshSticky?.();
        return;
      }
      root.dataset.aocdxUiReady = 'true';
      const gallery = root.querySelector('[data-aocdx-gallery]');
      if (gallery) initGallery(gallery);
      initFaqs(root);
      initMenu(root);
      initCommerce(root);
      initSticky(root);
    });
    initWheel();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:select', init);
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.('[data-aocdx-root]') ? event.target : event.target.querySelector?.('[data-aocdx-root]');
    root?.aocdxCleanups?.splice(0).forEach((cleanup) => cleanup());
    document.querySelectorAll('[data-aocdx-root]').forEach((candidate) => candidate.aocdxRefreshSticky?.(event.target));
  });
  document.addEventListener('shopify:block:select', (event) => {
    init();
    const orb = event.target?.closest?.('.aocdx-orb');
    const orbButton = orb?.querySelector('[data-ao-select]');
    if (orbButton) {
      const orbHost = orb.closest('.shopify-section');
      if (orbHost && orbHost.dataset.aocdxEditorOrb == null) {
        orbHost.dataset.aocdxEditorOrb = orbHost.querySelector('[data-ao-select][aria-pressed="true"]')?.getAttribute('data-ao-select') || '0';
      }
      if (orbButton.getAttribute('aria-pressed') !== 'true') orbButton.click();
      return;
    }
    const blockId = event.detail?.blockId;
    if (!blockId) return;
    const block = document.querySelector(`[data-aocdx-block-id="${CSS.escape(blockId)}"]`);
    const root = block?.closest('[data-aocdx-root]');
    if (!block || !root) return;
    if (block.matches('[data-aocdx-plan]')) {
      if (root.dataset.aocdxEditorPlan == null) {
        root.dataset.aocdxEditorPlan = root.querySelector('[data-aocdx-plan][aria-checked="true"]')?.dataset.aocdxPlan || '1';
      }
      root.aocdxSelectPlan?.(Number(block.dataset.aocdxPlan));
    } else if (block.matches('[data-aocdx-gallery-slide]')) {
      block.closest('[data-aocdx-gallery]')?.aocdxActivateGalleryMedia?.(block.dataset.aocdxGallerySlide, { announce: false });
    } else if (block.matches('[data-aocdx-faq-item]')) {
      const button = block.querySelector('[data-aocdx-faq-button]');
      if (block.dataset.aocdxEditorWasExpanded == null) {
        block.dataset.aocdxEditorWasExpanded = String(button?.getAttribute('aria-expanded') === 'true');
      }
      root.aocdxSetFaqExpanded?.(block, true);
    }
    block.classList.add('is-editor-selected');
    block.scrollIntoView({ block: 'nearest', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    const orbHost = event.target?.closest?.('.aocdx-orb')?.closest('.shopify-section');
    if (orbHost?.dataset.aocdxEditorOrb != null) {
      orbHost.querySelector(`[data-ao-select="${orbHost.dataset.aocdxEditorOrb}"]`)?.click();
      delete orbHost.dataset.aocdxEditorOrb;
      return;
    }
    const blockId = event.detail?.blockId;
    if (!blockId) return;
    const block = document.querySelector(`[data-aocdx-block-id="${CSS.escape(blockId)}"]`);
    if (!block) return;
    if (block.matches('[data-aocdx-faq-item]')) {
      const root = block.closest('[data-aocdx-root]');
      root?.aocdxSetFaqExpanded?.(block, block.dataset.aocdxEditorWasExpanded === 'true');
      delete block.dataset.aocdxEditorWasExpanded;
    } else if (block.matches('[data-aocdx-plan]')) {
      const planRoot = block.closest('[data-aocdx-root]');
      if (planRoot && planRoot.dataset.aocdxEditorPlan != null) {
        planRoot.aocdxSelectPlan?.(Number(planRoot.dataset.aocdxEditorPlan));
        delete planRoot.dataset.aocdxEditorPlan;
      }
    } else if (block.matches('[data-aocdx-gallery-slide]')) {
      const gallery = block.closest('[data-aocdx-gallery]');
      const initialMedia = gallery?.dataset.aocdxInitialMediaId || gallery?.querySelector('[data-aocdx-gallery-slide]')?.dataset.aocdxGallerySlide;
      if (gallery && initialMedia) gallery.aocdxActivateGalleryMedia?.(initialMedia, { announce: false, scrollThumb: false });
    }
    block.classList.remove('is-editor-selected');
  });
})();
